import { Mail, Phone, MapPin } from 'lucide-react';
import SEO from '../components/shared/SEO';

const LocationsPage = () => {
  const locations = [
    {
      city: 'RALEIGH',
      title: 'Queen B Logistics Headquarters',
      address: '3064 Wake Forest Road #1369',
      cityState: 'Raleigh, NC 27609',
      image: 'https://images.unsplash.com/photo-1676934556859-624fa21e2588?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxyYWxlaWdoJTIwbm9ydGglMjBjYXJvbGluYSUyMHNreWxpbmUlMjBuaWdodHxlbnwwfHx8fDE3NzAwMDkxODh8MA&ixlib=rb-4.1.0&q=85',
      mapEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d207447.34898804693!2d-78.8391145!3d35.7795897!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89ac5a2f9f51e0f7%3A0x6790b6528a11f0ad!2sRaleigh%2C%20NC!5e0!3m2!1sen!2sus!4v1234567890',
    },
    {
      city: 'CHARLOTTE',
      title: 'Queen B Logistics Resource Hub',
      address: '9805 Statesville Rd Suite 6046',
      cityState: 'Charlotte, NC 28269',
      image: 'https://images.unsplash.com/photo-1562762394-3acfba62a48e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxjaGFybG90dGUlMjBub3J0aCUyMGNhcm9saW5hJTIwc2t5bGluZSUyMG5pZ2h0fGVufDB8fHx8MTc3MDAwOTE5NHww&ixlib=rb-4.1.0&q=85',
      mapEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d208497.8039918539!2d-80.9817215!3d35.2270869!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88541fc4fc381a81%3A0x884650e6bf43d164!2sCharlotte%2C%20NC!5e0!3m2!1sen!2sus!4v1234567890',
    },
  ];

  return (
    <div data-testid="locations-page">
      <SEO 
        title="Our Locations - Raleigh & Charlotte, NC"
        description="Queen B Logistics serves owner-operators from our headquarters in Raleigh, NC and resource hub in Charlotte, NC. Find our office locations and contact information."
        canonical="https://queenblogistics.com/locations"
        keywords="freight dispatch Raleigh NC, trucking dispatch Charlotte NC, North Carolina dispatch service, Queen B Logistics locations"
      />
      {/* Page Header */}
      <section className="page-header">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="limited-badge mb-6">
            Our Locations
          </div>
          <h1 className="font-['Barlow_Condensed'] text-5xl md:text-6xl lg:text-7xl font-black text-white uppercase">
            Where We<br />
            <span className="text-[#D4AF37]">Operate</span>
          </h1>
        </div>
      </section>

      {/* Locations */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="space-y-24">
            {locations.map((location, index) => (
              <div 
                key={location.city}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
                data-testid={`location-${location.city.toLowerCase()}`}
              >
                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <h2 className="font-['Barlow_Condensed'] text-5xl md:text-6xl font-black text-white uppercase mb-4">
                    {location.city}
                  </h2>
                  <p className="text-[#D4AF37] text-lg mb-8">
                    {location.title}
                  </p>
                  
                  <div className="space-y-4 mb-8">
                    <div className="flex items-start gap-4">
                      <MapPin className="w-5 h-5 text-[#D4AF37] mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-white">{location.address}</p>
                        <p className="text-white/60">{location.cityState}</p>
                      </div>
                    </div>
                  </div>

                  {/* Map Thumbnail */}
                  <div className="aspect-video bg-[#121212] border border-white/10 overflow-hidden">
                    <iframe
                      src={location.mapEmbed}
                      width="100%"
                      height="100%"
                      style={{ border: 0, filter: 'grayscale(100%) invert(90%) contrast(90%)' }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title={`${location.city} Map`}
                    />
                  </div>
                </div>

                {/* Image */}
                <div className={`relative ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className="aspect-[4/3] overflow-hidden">
                    <img 
                      src={location.image} 
                      alt={`${location.city} Skyline`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
                  </div>
                  <div className="absolute bottom-4 left-4">
                    <span className="text-[#D4AF37] font-['Barlow_Condensed'] text-6xl font-black opacity-20">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Block */}
      <section className="py-16 md:py-24 bg-[#0A0A0A]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h3 className="font-['Barlow_Condensed'] text-3xl font-bold text-white uppercase mb-8">
            Contact Us
          </h3>
          <div className="flex flex-col md:flex-row justify-center gap-8 md:gap-16">
            <a 
              href="mailto:dispatch@queenblogistics.com" 
              className="flex items-center justify-center gap-3 text-white/70 hover:text-[#D4AF37] transition-colors"
            >
              <Mail className="w-5 h-5 text-[#D4AF37]" />
              <span>dispatch@queenblogistics.com</span>
            </a>
            <a 
              href="tel:+17163825540" 
              className="flex items-center justify-center gap-3 text-white/70 hover:text-[#D4AF37] transition-colors"
            >
              <Phone className="w-5 h-5 text-[#D4AF37]" />
              <span>(716) 382-5540</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LocationsPage;
